'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLoadedWebpSource } from '@/components/tools/webp-tool-shared';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import { flattenWebpSource, formatFileSize } from '@/lib/browser-webp-tools';
import { sanitizeHexColor } from '@/lib/browser-image-tools';
import type { FlattenBackgroundMode, GradientDirection } from '@/lib/browser-png-tools';

export default function BrowserFlattenWebpTransparency() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource } = useLoadedWebpSource();
  const [backgroundMode, setBackgroundMode] = useState<FlattenBackgroundMode>('white');
  const [customColor, setCustomColor] = useState('#ffffff');
  const [gradientStart, setGradientStart] = useState('#f4f4f4');
  const [gradientEnd, setGradientEnd] = useState('#d8d8d8');
  const [gradientDirection, setGradientDirection] = useState<GradientDirection>('to-bottom');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  const previewStyle = useMemo(
    () =>
      backgroundMode === 'white'
        ? { background: '#ffffff' }
        : backgroundMode === 'black'
          ? { background: '#000000' }
          : backgroundMode === 'custom-solid'
            ? { background: sanitizeHexColor(customColor) }
            : {
                background: `linear-gradient(${gradientDirection === 'to-right' ? 'to right' : gradientDirection === 'to-bottom-right' ? 'to bottom right' : gradientDirection === 'to-bottom-left' ? 'to bottom left' : 'to bottom'}, ${sanitizeHexColor(gradientStart)}, ${sanitizeHexColor(gradientEnd)})`,
              },
    [backgroundMode, customColor, gradientDirection, gradientEnd, gradientStart],
  );

  useEffect(() => {
    return () => {
      if (resultUrl) {
        URL.revokeObjectURL(resultUrl);
      }
    };
  }, [resultUrl]);

  function resetOutputState() {
    if (resultUrl) {
      URL.revokeObjectURL(resultUrl);
    }

    setResultUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(error instanceof Error ? error.message : 'The WebP could not be opened.');
    }
  }

  async function runFlattening() {
    if (!source) {
      setErrorMessage('Choose one WebP first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'flatten-webp-transparency',
        'Credits are not available for another WebP flattening export right now.',
      );
      const blob = await flattenWebpSource({
        file: source.file,
        mode: backgroundMode,
        customColor,
        gradientStart,
        gradientEnd,
        direction: gradientDirection,
      });
      const nextUrl = URL.createObjectURL(blob);

      resetOutputState();
      setResultUrl(nextUrl);
      setDownloadName(
        `${source.file.name.replace(/\.webp$/i, '') || source.file.name}-flattened.webp`,
      );
      setResultSize(blob.size);
      setCreditMessage(formatToolCreditSuccess('Flatten WebP transparency', credits));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The WebP could not be flattened.');
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Flatten WebP transparency onto a solid or gradient background</h2>
          <p>
            Replace WebP transparency with a chosen backdrop and export a ready-made WebP for
            channels that do not need alpha.
          </p>
        </div>
        <span className="tool-live-badge">Flatten</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : 'Choose one WebP or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Still and animated WebP are supported'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/webp,.webp"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Background mode</span>
              <select
                onChange={(event) => {
                  setBackgroundMode(event.target.value as FlattenBackgroundMode);
                  resetOutputState();
                }}
                value={backgroundMode}
              >
                <option value="white">White</option>
                <option value="black">Black</option>
                <option value="custom-solid">Custom solid</option>
                <option value="two-stop-gradient">2-stop linear gradient</option>
              </select>
            </label>
            {backgroundMode === 'custom-solid' ? (
              <label className="tool-live-field">
                <span>Custom color</span>
                <input
                  onChange={(event) => {
                    setCustomColor(sanitizeHexColor(event.target.value));
                    resetOutputState();
                  }}
                  type="text"
                  value={customColor}
                />
              </label>
            ) : null}
          </div>

          {backgroundMode === 'two-stop-gradient' ? (
            <>
              <div className="tool-live-controls">
                <label className="tool-live-field">
                  <span>Gradient start</span>
                  <input
                    onChange={(event) => {
                      setGradientStart(sanitizeHexColor(event.target.value));
                      resetOutputState();
                    }}
                    type="text"
                    value={gradientStart}
                  />
                </label>
                <label className="tool-live-field">
                  <span>Gradient end</span>
                  <input
                    onChange={(event) => {
                      setGradientEnd(sanitizeHexColor(event.target.value));
                      resetOutputState();
                    }}
                    type="text"
                    value={gradientEnd}
                  />
                </label>
              </div>
              <div className="tool-live-controls">
                <label className="tool-live-field">
                  <span>Direction</span>
                  <select
                    onChange={(event) => {
                      setGradientDirection(event.target.value as GradientDirection);
                      resetOutputState();
                    }}
                    value={gradientDirection}
                  >
                    <option value="to-bottom">Top to bottom</option>
                    <option value="to-right">Left to right</option>
                    <option value="to-bottom-right">Top left to bottom right</option>
                    <option value="to-bottom-left">Top right to bottom left</option>
                  </select>
                </label>
              </div>
            </>
          ) : null}

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runFlattening()}
              type="button"
            >
              {isProcessing ? 'Flattening…' : 'Flatten WebP'}
            </button>
            {resultUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={resultUrl}>
                Download WebP
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Flattening result</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Mode</strong>
              <span>{backgroundMode}</span>
            </div>
            <div>
              <strong>Type</strong>
              <span>
                {source
                  ? source.metadata.animated
                    ? 'Animated WebP'
                    : 'Still WebP'
                  : 'No file loaded'}
              </span>
            </div>
            <div>
              <strong>Result size</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
          </div>
        </aside>
      </div>

      <div className="tool-live-preview">
        <h3>Preview</h3>
        <div className="tool-image-compare-grid">
          <div className="tool-image-compare-card">
            <strong>Original WebP</strong>
            <div className="tool-live-preview-frame">
              {source ? (
                <img alt="Original WebP preview" src={source.previewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Original preview</strong>
                  <span>Upload one WebP to inspect the source alpha.</span>
                </div>
              )}
            </div>
          </div>
          <div className="tool-image-compare-card">
            <strong>Flattened WebP</strong>
            <div className="tool-live-preview-frame" style={previewStyle}>
              {resultUrl ? (
                <img alt="Flattened WebP preview" src={resultUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Flattened preview</strong>
                  <span>Run the export to preview the flattened WebP.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
